import React, { FC, ReactElement, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DsDialog, DsStack, DsTypography } from '@am92/react-design-system'

import withPageConstants, {
  IWithPageConstantsProps
} from '~/src/Hocs/withPageConstants'

import LoaderFullPage from '~/src/Components/LoaderFullPage'
import ResponsiveButton from '~/src/Components/ResponsiveButton'

import APP_CONFIG_CHECKER_PAGE_CONSTANTS from './AppConfigChecker.PageConstants'
import { T_APP_CONFIG_CHECKER_PAGE_CONSTANTS } from './AppConfigChecker.PageConstants/TYPES'

import { getAppVersion } from '~/src/Redux/AppConfig/Selectors'
import getAppVersionService, {
  getAppVersionServiceName
} from '~/src/Redux/AppConfig/Services/getAppVersion.Service'
import { isServiceLoading } from '~/src/Redux/ServiceTracker/Selectors'

import { TAppDispatch } from '~/src/Configurations/AppStore'
import { APP_ANDROID_URL, APP_MODE } from '~/src/Configurations/env'

export type TAppConfigCheckerProps = {
  children: ReactElement
}

const AppConfigChecker: FC<
  TAppConfigCheckerProps &
    IWithPageConstantsProps<T_APP_CONFIG_CHECKER_PAGE_CONSTANTS>
> = ({ PAGE_CONSTANTS, children }) => {
  if (APP_MODE !== 'CORDOVA') {
    return children
  }

  const dispatch = useDispatch<TAppDispatch>()
  const appVersion = useSelector(getAppVersion)
  const [checksPassed, setChecksPassed] = useState<boolean>(false)
  const [forceUpdate, setForceUpdate] = useState<boolean>(false)
  const [optionalUpdate, setOptionalUpdate] = useState<boolean>(false)
  const isLoading = useSelector(state =>
    isServiceLoading(state, [getAppVersionServiceName])
  )

  const handleDialogClose = () => {
    if (forceUpdate) return
    setOptionalUpdate(false)
    setChecksPassed(true)
  }

  const handleUpdateNow = () => {
    window.open(APP_ANDROID_URL, '_blank')
  }

  useEffect(() => {
    dispatch(getAppVersionService())
  }, [])

  useEffect(() => {
    if (!appVersion) return

    if (appVersion.status === 'LATEST') {
      setChecksPassed(true)
    } else if (
      appVersion.status === 'MAJOR_UPDATE' ||
      appVersion.status === 'MINOR_UPDATE'
    ) {
      setForceUpdate(true)
    } else if (appVersion.status === 'PATCH_UPDATE') {
      setOptionalUpdate(true)
    }
  }, [appVersion])

  if (isLoading) {
    return <LoaderFullPage />
  }

  if (forceUpdate || optionalUpdate) {
    return (
      <DsDialog
        open={forceUpdate || optionalUpdate}
        onClose={handleDialogClose}
        ContentProps={{
          sx: { marginTop: 'var(--ds-spacing-zero)' }
        }}
      >
        <DsStack
          direction='column'
          spacing='var(--ds-spacing-glacial)'
          sx={{
            mb: {
              xs: 'var(--ds-spacing-bitterCold)',
              md: 'var(--ds-spacing-warm)'
            }
          }}
        >
          <DsTypography component='div' variant='headingBoldSmall'>
            {forceUpdate
              ? PAGE_CONSTANTS.FORCE_UPDATE_HEADER
              : PAGE_CONSTANTS.OPTIONAL_UPDATE_HEADER}
          </DsTypography>
          <DsTypography component='div' variant='bodyRegularLarge'>
            {forceUpdate
              ? PAGE_CONSTANTS.FORCE_UPDATE_DESCRIPTION
              : PAGE_CONSTANTS.OPTIONAL_UPDATE_DESCRIPTION}
          </DsTypography>
        </DsStack>
        <DsStack direction='row' spacing='var(--ds-spacing-bitterCold)'>
          {!forceUpdate && (
            <ResponsiveButton
              fullWidth
              color='secondary'
              onClick={handleDialogClose}
            >
              {PAGE_CONSTANTS.OPTIONAL_UPDATE_CANCEL_BTN_TEXT}
            </ResponsiveButton>
          )}
          <ResponsiveButton fullWidth onClick={handleUpdateNow}>
            {forceUpdate
              ? PAGE_CONSTANTS.FORCE_UPDATE_CONFIRM_BTN_TEXT
              : PAGE_CONSTANTS.OPTIONAL_UPDATE_CONFIRM_BTN_TEXT}
          </ResponsiveButton>
        </DsStack>
      </DsDialog>
    )
  }

  return checksPassed ? children : null
}

export default withPageConstants(
  APP_CONFIG_CHECKER_PAGE_CONSTANTS,
  AppConfigChecker
)
